// Pure rekenlogica voor het dagplan op /vandaag: duur per taak, vrije tijd na
// afspraken, tijdblokken op de uurtijdlijn en de korte project-/taaknamen.
// Geen React, geen fetch — zodat alles hier los te testen is.
import { isDagstaatTaak } from '../../utils/taakStatus';

/** Werkdag waartegen "vrij" gerekend wordt (decimale uren). */
export const WERKDAG = { start: 9, eind: 17 };
/** Duur voor een taak zonder uren in Airtable. */
export const STANDAARD_MIN = 30;

/** 190 → "3u10", 45 → "45m", 120 → "2u". */
export function fmtDuur(min) {
  const m = Math.max(0, Math.round(Number(min) || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return `${r}m`;
  return r ? `${h}u${String(r).padStart(2, '0')}` : `${h}u`;
}

/** 9.25 → "09:15". */
export function fmtUur(h) {
  const tot = Math.round(h * 60);
  const uu = Math.floor(tot / 60);
  const mm = tot % 60;
  return `${String(uu).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/** Invoer "1u30", "1:30", "90", "90m", "1.5u", "2u" → minuten (null bij onzin). */
export function parseDuur(tekst) {
  const s = String(tekst || '').trim().toLowerCase().replace(',', '.');
  if (!s) return null;
  let m = s.match(/^(\d+)\s*[:u]\s*(\d{1,2})\s*m?$/);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  m = s.match(/^(\d+(?:\.\d+)?)\s*(u|h|uur)$/);
  if (m) return Math.round(Number(m[1]) * 60);
  m = s.match(/^(\d+)\s*(m|min)?$/);
  if (m) return Number(m[1]);
  return null;
}

/** Afronden op een kwartier (decimale uren). */
export function snapKwartier(h) {
  return Math.round(h * 4) / 4;
}

/** Werkdagen (ma–vr) van start t/m eind, minimaal 1. Datums als YYYY-MM-DD. */
export function werkdagenTussen(start, eind) {
  if (!start || !eind || eind <= start) return 1;
  const d = new Date(`${start.slice(0, 10)}T12:00:00`);
  const e = new Date(`${eind.slice(0, 10)}T12:00:00`);
  let n = 0;
  for (let i = 0; d <= e && i < 400; i += 1) {
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) n += 1;
    d.setDate(d.getDate() + 1);
  }
  return Math.max(1, n);
}

/**
 * Hoeveel minuten een taak vandaag kost.
 * - eigen: de gebruiker heeft de duur hier zelf gezet (lokaal)
 * - uren: UrenWerk van een ééndaagse taak
 * - dagdeel: UrenWerk verdeeld over de werkdagen van start t/m eind
 * - standaard: geen uren bekend → STANDAARD_MIN
 */
export function taakMinuten(taak, eigen = {}) {
  if (taak && eigen[taak.id] != null) return { min: eigen[taak.id], bron: 'eigen' };
  const uren = Number(taak?.hours) || 0;
  if (uren > 0) {
    const eind = taak?._raw?.fields?.EndDateUitvoerder || null;
    const n = werkdagenTussen(taak.startDate, eind);
    const min = Math.max(15, Math.round((uren * 60) / n / 15) * 15);
    return { min, bron: n > 1 ? 'dagdeel' : 'uren', dagen: n };
  }
  return { min: STANDAARD_MIN, bron: 'standaard' };
}

/** Decimaal uur uit een ISO-string. Zonder offset (Graph in NL-tijd) lezen we HH:MM letterlijk. */
export function uurVan(iso) {
  if (!iso) return null;
  const s = String(iso);
  if (/[zZ]$|[+-]\d\d:?\d\d$/.test(s)) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    return d.getHours() + d.getMinutes() / 60;
  }
  const m = s.match(/T(\d{2}):(\d{2})/);
  return m ? Number(m[1]) + Number(m[2]) / 60 : null;
}

/**
 * Samengevoegde bezette intervallen [[s, e]] binnen [van, tot], uit afspraken
 * met { s, e, vrij }. Afspraken die als "vrij" in de agenda staan tellen niet.
 */
export function bezetteIntervallen(afspraken, van = WERKDAG.start, tot = WERKDAG.eind) {
  const ruw = (afspraken || [])
    .filter((a) => a && !a.vrij && a.s != null && a.e != null)
    .map((a) => [Math.max(van, a.s), Math.min(tot, a.e)])
    .filter(([s, e]) => e > s)
    .sort((a, b) => a[0] - b[0]);
  const uit = [];
  ruw.forEach(([s, e]) => {
    const laatste = uit[uit.length - 1];
    if (laatste && s <= laatste[1]) laatste[1] = Math.max(laatste[1], e);
    else uit.push([s, e]);
  });
  return uit;
}

/** Minuten vrij op de werkdag na afspraken. */
export function vrijeMinuten(afspraken, werkdag = WERKDAG) {
  const bezet = bezetteIntervallen(afspraken, werkdag.start, werkdag.eind)
    .reduce((som, [s, e]) => som + (e - s), 0);
  return Math.max(0, Math.round((werkdag.eind - werkdag.start - bezet) * 60));
}

/**
 * Tijdblokken voor de tijdlijn. taken = [{ id, min, vast }] in planvolgorde;
 * `vast` is een zelf gekozen begintijd (decimaal uur) of null. Vaste blokken
 * staan op hun tijd; de rest schuift in volgorde in de eerste vrije gaten vanaf
 * `vanaf` (meestal nu of begin werkdag), om afspraken en vaste blokken heen.
 * Returnt [{ id, s, e, vast, over }] — over = loopt voorbij het einde van de werkdag.
 */
export function planBlokken(taken, afspraken, { vanaf = WERKDAG.start, eind = WERKDAG.eind } = {}) {
  const blokken = [];
  const bezet = (afspraken || [])
    .filter((a) => a && !a.vrij && a.s != null && a.e != null)
    .map((a) => [a.s, a.e]);
  (taken || []).forEach((t) => {
    if (t.vast == null) return;
    const e = t.vast + t.min / 60;
    blokken.push({ id: t.id, s: t.vast, e, vast: true, over: e > eind + 1e-9 });
    bezet.push([t.vast, e]);
  });
  let cursor = vanaf;
  (taken || []).forEach((t) => {
    if (t.vast != null) return;
    const duur = t.min / 60;
    let s = cursor;
    let verschoven = true;
    for (let guard = 0; verschoven && guard < 200; guard += 1) {
      verschoven = false;
      for (let i = 0; i < bezet.length; i += 1) {
        const [bs, be] = bezet[i];
        if (s < be - 1e-9 && s + duur > bs + 1e-9) { s = be; verschoven = true; }
      }
    }
    const e = s + duur;
    blokken.push({ id: t.id, s, e, vast: false, over: e > eind + 1e-9 });
    bezet.push([s, e]);
    cursor = e;
  });
  return blokken.sort((a, b) => a.s - b.s);
}

/** Einddatum waarop een taak "te laat" is: eind uitvoerder → deadline → start. */
export function einddatum(taak) {
  const f = taak?._raw?.fields || {};
  const d = f.EndDateUitvoerder || taak?.deadline || taak?.startDate || null;
  return d ? String(d).slice(0, 10) : null;
}

/** Te laat = de einddatum ligt vóór vandaag (een lopende meerdaagse taak is dat niet).
 *  Een draaidag of vrije dag is nooit te laat: voorbij is voorbij (Joris, 3 nov 2026). */
export function isAchterstallig(taak, vandaag) {
  if (!taak || taak.isComplete) return false;
  if (isDagstaatTaak(taak) || isDagstaatTaak(taak._raw)) return false;
  const eind = einddatum(taak);
  return !!eind && eind < vandaag;
}

/** Een draaidag of vrije dag die al voorbij is (einddatum vóór vandaag): hoort nergens meer als werk. */
export function isVoorbijeDagstaat(taak, vandaag) {
  if (!taak || !(isDagstaatTaak(taak) || isDagstaatTaak(taak._raw))) return false;
  const eind = einddatum(taak);
  return !!eind && eind < vandaag;
}

/**
 * "9262. Only Creative Entrepreneurs B.V. - Only Brandmovie Investeerders"
 * → { nummer: "9262", kort: "Only Brandmovie Investeerders", label: "9262 · Only Brandmovie Investeerders" }
 */
export function projectTag(naam) {
  if (!naam) return null;
  const s = String(naam).trim();
  const m = s.match(/^(\d{3,6})\.?\s*(.*)$/);
  const nummer = m ? m[1] : null;
  const rest = (m ? m[2] : s).trim();
  const i = rest.lastIndexOf(' - ');
  const kort = (i > 0 ? rest.slice(i + 3) : rest).trim() || rest;
  return { nummer, kort, label: nummer ? `${nummer} · ${kort}` : kort };
}

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** "Script v2 - Brandmovie Investeerders" binnen project "... Only Brandmovie Investeerders" → "Script v2". */
export function korteTitel(titel, projectNaam) {
  const t = String(titel || '').trim();
  const i = t.lastIndexOf(' - ');
  if (i <= 0 || !projectNaam) return t;
  const staart = norm(t.slice(i + 3));
  if (staart.length >= 4 && norm(projectNaam).includes(staart)) return t.slice(0, i).trim();
  return t;
}

/** Items in de bewaarde volgorde (ids); onbekende achteraan in hun eigen volgorde. */
export function ordenen(items, volgorde = []) {
  const pos = new Map((volgorde || []).map((id, i) => [id, i]));
  return (items || [])
    .map((it, i) => ({ it, i }))
    .sort((a, b) => {
      const pa = pos.has(a.it.id) ? pos.get(a.it.id) : 1e6 + a.i;
      const pb = pos.has(b.it.id) ? pos.get(b.it.id) : 1e6 + b.i;
      return pa - pb;
    })
    .map((x) => x.it);
}

/** Groepeer op project in volgorde van eerste voorkomen. */
export function groepeerOpProject(items) {
  const groepen = new Map();
  (items || []).forEach((it) => {
    const sleutel = it.projectName || '';
    if (!groepen.has(sleutel)) groepen.set(sleutel, []);
    groepen.get(sleutel).push(it);
  });
  return [...groepen.entries()].map(([project, lijst]) => ({ project: project || null, items: lijst }));
}

/**
 * Het deel van de werkdag dat nog over is: vóór de start de hele dag, daarna
 * vanaf nu (op het kwartier naar boven), na het einde niets.
 */
export function restVanDag(nu, werkdag = WERKDAG) {
  if (nu == null || nu <= werkdag.start) return { start: werkdag.start, eind: werkdag.eind, heleDag: true };
  const start = Math.min(werkdag.eind, Math.ceil(nu * 4) / 4);
  return { start, eind: werkdag.eind, heleDag: false };
}

/**
 * Welke taken naar morgen moeten om binnen de vrije tijd te blijven: van achter
 * naar voren in planvolgorde, eerst taken zonder vast tijdblok, tot het teveel
 * gedekt is. taken = [{ id, min, vast }]. Returnt ids in planvolgorde.
 */
export function teVerschuiven(taken, teveelMin) {
  if (!(teveelMin > 0)) return [];
  const gekozen = new Set();
  let gedekt = 0;
  const kies = (lijst) => {
    for (let i = lijst.length - 1; i >= 0 && gedekt < teveelMin; i -= 1) {
      if (!gekozen.has(lijst[i].id)) { gekozen.add(lijst[i].id); gedekt += lijst[i].min; }
    }
  };
  kies((taken || []).filter((t) => t.vast == null));
  kies(taken || []);
  return (taken || []).filter((t) => gekozen.has(t.id)).map((t) => t.id);
}

/** Volgende werkdag (ma–vr) na `datum` — vrijdag/zaterdag/zondag → maandag (zoals Weekplan). */
export function volgendeWerkdag(datum = new Date()) {
  const d = new Date(datum);
  d.setHours(12, 0, 0, 0);
  do { d.setDate(d.getDate() + 1); } while (d.getDay() === 0 || d.getDay() === 6);
  return d;
}

const WEEKDAGEN = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
/** "morgen" als de volgende werkdag letterlijk morgen is, anders de dagnaam ("maandag"). */
export function volgendeWerkdagNaam(datum = new Date()) {
  const volgende = volgendeWerkdag(datum);
  const morgen = new Date(datum);
  morgen.setHours(12, 0, 0, 0);
  morgen.setDate(morgen.getDate() + 1);
  return volgende.toDateString() === morgen.toDateString() ? 'morgen' : WEEKDAGEN[volgende.getDay()];
}

// ─── Correctheid (Codex-review planning-gauntlet, 25 sep) ───────────────────

/**
 * Start en eind zoals de taak nu in beeld staat. Is de start lokaal verschoven
 * (optimistisch, _raw nog oud), dan is de taak door die verplaatsing ééndaags gemaakt.
 */
export function datumBereik(taak) {
  const f = taak?._raw?.fields || {};
  const start = String(taak?.startDate || f.StartDateUitvoerder || '').slice(0, 10) || null;
  const rauwStart = String(f.StartDateUitvoerder || '').slice(0, 10) || null;
  const eind = rauwStart === start ? (String(f.EndDateUitvoerder || '').slice(0, 10) || start) : start;
  return { start, eind };
}

/** Meerdaags = de einddatum ligt ná de startdatum (uit start/eind, niet uit de uren). */
export function isMeerdaags(taak) {
  const { start, eind } = datumBereik(taak);
  return !!start && !!eind && eind > start;
}

/**
 * Waar een nieuwe duur heen gaat. Alleen een ééndaagse taak krijgt hem als UrenWerk;
 * bij een meerdaagse is "2 uur vandaag" jouw dagschatting, niet het totaal van de taak.
 */
export function duurDoel(taak) {
  if (!taak?._airtableId || isMeerdaags(taak)) return 'lokaal';
  return 'taak';
}

/** De ruwe UrenWerk-waarde voor een exacte undo: leeg blijft null, 0 blijft 0. */
export function ruweUren(taak) {
  const r = taak?._raw?.fields?.UrenWerk;
  if (r === undefined || r === null || r === '') return null;
  const n = Number(r);
  return Number.isFinite(n) ? n : null;
}

/** Taak met een nieuwe UrenWerk in state: hours én _raw bijgewerkt, zodat een volgende undo klopt. */
export function metUren(taak, waarde) {
  const raw = taak?._raw || {};
  return {
    ...taak,
    hours: parseFloat(waarde) || null,
    _raw: { ...raw, fields: { ...(raw.fields || {}), UrenWerk: waarde } },
  };
}

/** Promise.allSettled-uitkomsten terug naar de items: welke lukten, welke niet. */
export function splitsUitkomsten(items, uitkomsten) {
  const gelukt = [];
  const mislukt = [];
  (items || []).forEach((it, i) => {
    (uitkomsten?.[i]?.status === 'fulfilled' ? gelukt : mislukt).push(it);
  });
  return { gelukt, mislukt };
}

/** "3 taken naar morgen" / "2 van 3 naar morgen verplaatst" / "Niets naar morgen verplaatst". */
export function bulkMelding(gelukt, totaal, waarheen) {
  if (!gelukt) return `Niets naar ${waarheen} verplaatst`;
  if (gelukt === totaal) return `${gelukt === 1 ? '1 taak' : `${gelukt} taken`} naar ${waarheen}`;
  return `${gelukt} van ${totaal} naar ${waarheen} verplaatst`;
}

/** Intern project = Full Frame zelf ("1234. Full Frame – …"). */
export function isInternProject(naam) {
  if (!naam) return false;
  return String(naam).replace(/^\d+\.\s*/, '').startsWith('Full Frame');
}

/**
 * Hoeveel interne deadlines er over vandaag + achterstallig samen zijn — de schakelaar
 * hangt hieraan, zodat een interne deadline van vandaag ook bereikbaar is als er geen
 * interne achterstallige is. Losse taken zonder deliverable tellen niet (worden nergens getoond).
 */
export function aantalInterneDeadlines(...lijsten) {
  const gezien = new Set();
  lijsten.flat().forEach((d) => {
    if (!d || (d.type === 'task' && !d.deliverableId)) return;
    if (isInternProject(d.projectName)) gezien.add(d.id ?? d);
  });
  return gezien.size;
}

/**
 * Welke taken de bulkactie "naar morgen" mag verzetten. Die zet start én eind op
 * morgen; bij een meerdaagse taak zou dat het hele bereik op één dag proppen (en de
 * uren van dagen werk in één dag). Meerdaagse taken doen daarom niet mee — ze lopen
 * vanzelf door naar morgen.
 */
export function naarMorgenKandidaten(taken) {
  return (taken || []).filter((t) => !isMeerdaags(t));
}

/**
 * "Ga naar project" vanuit een deadline: een deliverable opent het project mét het
 * deliverables-paneel open en die deliverable gemarkeerd (wiki 27 sep: je landde op de
 * huidige fase en moest de deliverable zelf zoeken). Andere deadlines: gewoon het project.
 */
export function projectUrlVoorDeadline(d) {
  if (!d?.projectId) return '/projects';
  const basis = `/projects/${encodeURIComponent(d.projectId)}`;
  if (d.type !== 'deliverable' || !d.id) return basis;
  const q = new URLSearchParams({ paneel: 'deliverables', deliverable: String(d.id) });
  return `${basis}?${q.toString()}`;
}
